import React, {
    useState,
    useContext,
    useCallback,
    useRef,
    useMemo,
    Fragment,
    useEffect,
} from "react";
import moment from "moment";
import AuthContext from "../../contexts/auth-context.js";
import { useNavigate } from "react-router-dom";
import {
    formatTimestamp,
    generateDay,
    getTeam,
} from "../../util.js";
import ConcessionOverviewChart from "./charts/ConcessionOverviewChart.js";
import { getConcessionAPI } from "./dummyLiveData.js";
import { toVenueTime } from "./live/liveMetrics";
import { LinearProgress } from "@mui/material";
import ConcessionOverviewReportGenerator from "./components/report_generation/ConcessionOverviewReportGenerator.js";
import Icon from "../../components/atoms/Icon.js";
import { Panel, PanelHeader, InfoTip } from "./components/ConcessionUI";
import SectionCompareCards from "./components/SectionCompareCards";
import { getConcessionExtension } from "./extensions/Slot";
import OverviewInsights, { buildInsights } from "./components/OverviewInsights";
import Presence from "./components/Presence";
import { getQueueTimeStatus, getPeopleCountStatus, STATUS_COLORS, QUEUE_TIME_GOOD_MAX, formatQueueValue } from "./concessionTheme";
import { seriesBySection, averageSeries, minutesOver, overflowSummary, formatClock, formatDuration } from "./live/liveMetrics";
import { KpiCard, Sparkline, unitFormatter, StatusPill, OverflowPill, TileDetails } from "./live/LiveComponents";

// People count thresholds (same as the game day views)
const COUNT_AVG_LOW = 20;
const COUNT_AVG_HIGH = 40;
const QUEUE_STATUS_LABELS = { good: 'On target', bad: 'Over target' };
const COUNT_STATUS_LABELS = { good: 'Light', warn: 'Moderate', bad: 'Busy' };

const overviewColors = [
    '#3e58c0', // Indigo
    '#e88559', // Tangerine
    '#9792ec', // Lilac
    '#aa4851', // Scarlet
    '#866263', // Royal Purple
    '#76ba92', // Key Lime
    '#0bcaca', // Light Turquoise
    '#f1dc78', // Lemon Yellow
    '#3c6e5f', // Army Green
];

// Helper function to format time offset for display
export const formatTimeOffset = (minutes) => {
    if (minutes === 0) return 'Game Start';
    
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    
    let timeString;
    if (mins === 0) {
        timeString = `${hours}h`;
    } else {
        timeString = `${hours}h ${mins}m`;
    }
    
    if (minutes > 0) {
        return `+${timeString}`;
    } else {
        return `-${timeString}`;
    }
};

// Section Tabbed Charts Component
const SectionTabbedCharts = ({ 
    concessionDataList, 
    gameTitles, 
    gameStartTimes, 
    isCountMode, 
    timeRangeFilter, 
    currentLocation,
    games = [],
    formatTransactionTime,
    authCtx,
    extension,
}) => {
    const [activeTab, setActiveTab] = useState(0);

    // Extract all unique sections from the data and sort numerically
    const allSections = useMemo(() => {
        const sectionSet = new Set();
        
        concessionDataList.forEach(gameData => {
            const dataArrays = isCountMode 
                ? gameData.historicalCountData || []
                : [
                    ...(gameData.historicalQueueData || []),
                    ...(gameData.historicalTransactionData || [])
                ];
            
            dataArrays.forEach(sectionData => {
                if (sectionData.section) {
                    sectionSet.add(sectionData.section);
                }
            });
        });
        
        // Sort sections numerically
        return Array.from(sectionSet).sort((a, b) => {
            const numA = parseInt(a.replace(/[^\d]/g, ''));
            const numB = parseInt(b.replace(/[^\d]/g, ''));
            return numA - numB;
        });
    }, [concessionDataList, isCountMode]);

    // Reset active tab if it's out of bounds
    useEffect(() => {
        if (activeTab >= allSections.length && allSections.length > 0) {
            setActiveTab(0);
        }
    }, [allSections.length, activeTab]);

    if (allSections.length === 0) {
        return (
            <div style={{
                padding: '60px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '18px'
            }}>
                No Section Data Available
            </div>
        );
    }

    const activeSection = allSections[activeTab];

    // Prepare data for the active section
    const prepareSectionData = (sectionId, dataType) => {
        return concessionDataList.map(gameData => {
            const dataArrays = dataType === 'count' 
                ? gameData.historicalCountData || []
                : dataType === 'queue'
                    ? gameData.historicalQueueData || []
                    : gameData.historicalTransactionData || [];
            
            const sectionData = dataArrays.find(section => section.section === sectionId);
            return sectionData ? [sectionData] : [];
        });
    };

    return (
        <div className="cx-ov-charts">
            <Panel className="cx-ov-sections">
                <PanelHeader
                    title="Sections"
                    subtitle="Each section compared across the selected games, busiest first. Select a section to see its full charts."
                    info="Bars show each game's average for that section over the time window, all on the same scale. The mini chart lines the games up by game start."
                />
                {/* Card titles are the section selectors (and the tabs the PDF report steps through) */}
                <SectionCompareCards
                    sections={allSections}
                    games={games}
                    isCountMode={isCountMode}
                    activeSection={activeSection}
                    onSelectSection={(id) => setActiveTab(allSections.indexOf(id))}
                    authCtx={authCtx}
                    currentLocation={currentLocation}
                    extension={extension}
                />

                <div className="cx-ov-detail-head">
                    <Icon name="graph-up" />
                    Section {activeSection} over time
                </div>
            {/* Active Section Charts */}
            <div>
                {isCountMode ? (
                    // Count Mode: Single chart
                    <ConcessionOverviewChart
                        multipleHistoricalData={prepareSectionData(activeSection, 'count')}
                        gameTitles={gameTitles}
                        gameStartTimes={gameStartTimes}
                        chartTitle={`People Count - Section ${activeSection}`}
                        yAxisLabel="People Count"
                        isCountMode={true}
                        timeRangeFilter={timeRangeFilter}
                    />
                ) : (
                    // Queue Mode: Two charts (queue + transaction)
                    <>
                        <ConcessionOverviewChart
                            multipleHistoricalData={prepareSectionData(activeSection, 'queue')}
                            gameTitles={gameTitles}
                            gameStartTimes={gameStartTimes}
                            chartTitle={`Queue Times - Section ${activeSection}`}
                            yAxisLabel="Queue Time (mins)"
                            isCountMode={false}
                            timeRangeFilter={timeRangeFilter}
                        />
                        
                        <ConcessionOverviewChart
                            multipleHistoricalData={prepareSectionData(activeSection, 'transaction')}
                            gameTitles={gameTitles.map(title => `${title} - Transaction`)}
                            gameStartTimes={gameStartTimes}
                            chartTitle={`Transaction Times - Section ${activeSection}`}
                            yAxisLabel="Transaction Time (seconds)"
                            isCountMode={false}
                            timeRangeFilter={timeRangeFilter}
                        />
                    </>
                )}
            </div>
            </Panel>
        </div>
    );
};

const SeasonSelector = ({ games, currentSeason, setCurrentSeason }) => {
    const [availableSeasons, setAvailableSeasons] = useState([]);

    // Extract all unique seasons from games data
    useEffect(() => {
        const years = new Set();
        games.forEach(game => {
            game.dates.forEach(date => {
                years.add(moment(date.date).format('YYYY'));
            });
        });
        setAvailableSeasons(Array.from(years).sort((a, b) => b - a)); // Sort descending (newest first)
    }, [games]);

    return (
        <div className="cx-season">
            <span className="cx-chart-segment-label">Season</span>
            <div className="cx-chart-segmented">
                {availableSeasons.map((season) => (
                    <button
                        key={season}
                        type="button"
                        onClick={() => setCurrentSeason(season)}
                        className={`cx-chart-segment ${currentSeason === season ? "active" : ""}`}
                    >
                        {season}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Time window relative to game start: presets + custom start/end
export const WINDOW_PRESETS = [
    { key: 'full', label: 'Full game', start: -180, end: 240 },
    // Relative to game start, so not "pregame" (pregame is before gates open)
    { key: 'pre', label: 'Before start', start: -180, end: 0 },
    { key: 'in', label: 'In-game', start: 0, end: 240 },
];

export const TimeWindow = ({ startOffset, endOffset, onStartChange, onEndChange, onPreset, info, compact = false, action = null, detail = null }) => {
    const timeOptions = [];

    // Generate options from -8 hours to +8 hours in 30-minute increments
    for (let hours = -8; hours <= 8; hours += 0.5) {
        const totalMinutes = Math.round(hours * 60);
        timeOptions.push({ value: totalMinutes, label: formatTimeOffset(totalMinutes) });
    }

    const activePreset = WINDOW_PRESETS.find((p) => p.start === startOffset && p.end === endOffset);

    return (
        <div className={`cx-ov-window${compact ? ' compact' : ''}`}>
            <div className="cx-ov-window-title">
                <div className="cx-ov-window-title-text">
                    <div className="cx-panel-title-row">
                        {compact
                            ? <span className="cx-ov-bar-label">Time window</span>
                            : <h2 className="cx-panel-title">Time window</h2>}
                        {info}
                    </div>
                    <p className="cx-panel-subtitle">
                        {activePreset ? activePreset.label : 'Custom'}: {formatTimeOffset(startOffset)} to {formatTimeOffset(endOffset)} relative to each game's start
                    </p>
                    {detail}
                </div>
                {action && <div className="cx-ov-window-action">{action}</div>}
            </div>
            <div className="cx-ov-window-controls">
                <div className="cx-chart-segmented" role="group" aria-label="Time window presets">
                    {WINDOW_PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            className={`cx-chart-segment ${activePreset?.key === p.key ? 'active' : ''}`}
                            onClick={() => onPreset(p.start, p.end)}
                        >
                            {p.label}
                        </button>
                    ))}
                    <span className={`cx-chart-segment cx-aw-custom ${!activePreset ? 'active' : ''}`}>Custom</span>
                </div>
                <label className="cx-field">
                    <span>From</span>
                    <select
                        className="form-select form-select-sm"
                        value={startOffset}
                        onChange={(e) => onStartChange(parseInt(e.target.value))}
                    >
                        {timeOptions.map(option => (
                            <option key={`start-${option.value}`} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="cx-field">
                    <span>To</span>
                    <select
                        className="form-select form-select-sm"
                        value={endOffset}
                        onChange={(e) => onEndChange(parseInt(e.target.value))}
                    >
                        {timeOptions.map(option => (
                            <option key={`end-${option.value}`} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        </div>
    );
};

export default function ConcessionOverview({
    currentLocation,
    currentTime,
    sort,
    games,
}) {
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);

    // Determine if we're in count mode or queue mode
    const isCountMode = currentLocation?.sensorDataType === "count";

    // Initialize current season state
    // "Add game" dropdown
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const onDown = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
        };
        const onKey = (e) => e.key === 'Escape' && setPickerOpen(false);
        window.addEventListener('pointerdown', onDown);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerdown', onDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [pickerOpen]);

    const [currentSeason, setCurrentSeason] = useState(() => {
        const years = new Set();
        games.forEach(game => {
            game.dates.forEach(date => {
                years.add(moment(date.date).format('YYYY'));
            });
        });
        const sortedYears = Array.from(years).sort((a, b) => b - a);
        return sortedYears[0] || moment().format('YYYY'); // Default to current year if no games
    });

    // Time range state (in minutes from first pitch)
    const [timeRange, setTimeRange] = useState({
        startOffset: -180, // -3 hours
        endOffset: 240,    // +4 hours
        isFullRange: false
    });

    const [overviewState, setOverviewState] = useState({
        selectedDates: [],
        concessionDataList: [], // Stores processed concession data for each game
        gameTitlesList: [],
        gameStartTimes: [],
        loading: false,
    });

    // Max is 4 games: close the picker as soon as the 4th is added
    const selectedCount = overviewState.selectedDates.length;
    useEffect(() => {
        if (selectedCount >= 4) setPickerOpen(false);
    }, [selectedCount]);

    // Empty "Add a game" slots open the picker in the compare bar
    const barRef = useRef(null);
    const openPicker = () => {
        setPickerOpen(true);
        barRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const dataCache = useRef({});

    // Helper function to convert game start time to minutes from start of day
    const gameStartTimeToMinutes = useCallback((gameStartTime) => {
        if (!gameStartTime) return null;
        const [hours, minutes] = gameStartTime.split(':').map(Number);
        return hours * 60 + minutes;
    }, []);

    // Helper function to convert timestamp to minutes
    // Parses directly from the venue-local timestamp string to avoid browser timezone issues
    const timestampToMinutes = useCallback((timestamp) => {
        const timePart = timestamp.split('T')[1]; // "18:30"
        if (!timePart) return 0;
        const [hours, minutes] = timePart.split(':').map(Number);

        // For times after midnight (0-7), add 24 hours worth of minutes
        if (hours >= 0 && hours < 8) {
            return (hours + 24) * 60 + minutes;
        } else {
            return hours * 60 + minutes;
        }
    }, []);

    // Function to filter data points based on time range relative to game start
    const filterDataByTimeRange = useCallback((gameData, gameStartTime) => {
        if (!gameStartTime || timeRange.isFullRange) {
            return gameData; // Return all data if no game start time or full range selected
        }

        const gameStartMinutes = gameStartTimeToMinutes(gameStartTime);
        if (gameStartMinutes === null) return gameData;

        const filteredData = { ...gameData };

        // Filter historical data arrays
        if (gameData.historicalCountData) {
            filteredData.historicalCountData = gameData.historicalCountData.map(sectionData => ({
                ...sectionData,
                data: sectionData.data.filter(point => {
                    const itemMinutes = timestampToMinutes(point.x);
                    const offsetFromGameStart = itemMinutes - gameStartMinutes;
                    return offsetFromGameStart >= timeRange.startOffset && offsetFromGameStart <= timeRange.endOffset;
                })
            })).filter(sectionData => sectionData.data.length > 0); // Remove sections with no data
        }

        if (gameData.historicalQueueData) {
            filteredData.historicalQueueData = gameData.historicalQueueData.map(sectionData => ({
                ...sectionData,
                data: sectionData.data.filter(point => {
                    const itemMinutes = timestampToMinutes(point.x);
                    const offsetFromGameStart = itemMinutes - gameStartMinutes;
                    return offsetFromGameStart >= timeRange.startOffset && offsetFromGameStart <= timeRange.endOffset;
                })
            })).filter(sectionData => sectionData.data.length > 0);
        }

        if (gameData.historicalTransactionData) {
            filteredData.historicalTransactionData = gameData.historicalTransactionData.map(sectionData => ({
                ...sectionData,
                data: sectionData.data.filter(point => {
                    const itemMinutes = timestampToMinutes(point.x);
                    const offsetFromGameStart = itemMinutes - gameStartMinutes;
                    return offsetFromGameStart >= timeRange.startOffset && offsetFromGameStart <= timeRange.endOffset;
                })
            })).filter(sectionData => sectionData.data.length > 0);
        }

        return filteredData;
    }, [timeRange, gameStartTimeToMinutes, timestampToMinutes]);

    const getCachedData = (dateToFetch) => {
        const cachedDataString = localStorage.getItem(`concessionOverviewData-${dateToFetch}`);
        if (cachedDataString) {
            try {
                return JSON.parse(cachedDataString);
            } catch (error) {
                console.error("Error parsing cached data from localStorage", error);
                return null;
            }
        }
        return null;
    };

    const saveCachedData = (dateToFetch, data) => {
        try {
            localStorage.setItem(`concessionOverviewData-${dateToFetch}`, JSON.stringify(data));
        } catch (error) {
            console.error("Error saving data to localStorage", error);
        }
    };

    const handleOverviewGameClick = (gameDate, game) => {
        const gameIndex = game.dates.findIndex((g) => g.date === gameDate.date);
        const teamNameAndGame =
            gameIndex !== -1 ? `${game.teamName} · Game ${gameIndex + 1}` : "Game not found";
        
        // Get the game start time for this specific date
        const gameStartTime = gameIndex !== -1 ? gameDate.gameStartTime : null;

        const dateExists = overviewState.selectedDates.includes(gameDate.date);

        if (dateExists) {
            const indexToRemove = overviewState.selectedDates.indexOf(gameDate.date);
            setOverviewState((prevState) => ({
                selectedDates: prevState.selectedDates.filter((_, index) => index !== indexToRemove),
                concessionDataList: prevState.concessionDataList.filter((_, index) => index !== indexToRemove),
                gameTitlesList: prevState.gameTitlesList.filter((title) => title !== teamNameAndGame),
                gameStartTimes: prevState.gameStartTimes?.filter((_, index) => index !== indexToRemove) || [],
            }));
        } else {
            if (overviewState.selectedDates.length >= 4) {
                alert("Maximum amount of games is 4.");
                return;
            }

            setOverviewState((prevState) => ({
                selectedDates: [...prevState.selectedDates, gameDate.date],
                concessionDataList: prevState.concessionDataList,
                gameTitlesList: [...prevState.gameTitlesList, teamNameAndGame],
                gameStartTimes: [...(prevState.gameStartTimes || []), gameStartTime],
            }));

            fetchDataForGraph(gameDate.date);
        }
    };

    const fetchDataForGraph = (dateToFetch) => {
        const localStorageData = getCachedData(dateToFetch);
        if (localStorageData) {
            dataCache.current[dateToFetch] = localStorageData;
            setOverviewState((prevState) => ({
                ...prevState,
                concessionDataList: [...prevState.concessionDataList, localStorageData.processedData],
            }));
            return;
        }

        setOverviewState((prevState) => ({ ...prevState, loading: true }));

        // Always use v2 route
        const concessionDataPromise = new Promise((resolve, reject) => {
            // Same as util's getQueuesAPI unless dummy data / test mode is on (local dev only)
            getConcessionAPI(currentLocation)(
                "get-concession-location-daily-data-v2",
                {
                    sections: currentLocation.sections.map(section => section.sectionId),
                    venue: authCtx.venue,
                    locationId: currentLocation.id,
                    measurement: currentLocation.measurement || "",
                    timezone: currentLocation.timeZone,
                    date: moment(dateToFetch).format("yyyy-MM-DD")
                },
                (data, error) => {
                    if (error) {
                        if (error.status === 401) {
                            authCtx.logout();
                            navigate("/auth");
                        } else {
                            alert("Unable to fetch data. Please refresh the page and try again.");
                        }
                        reject(error);
                    } else if (data) {
                        try {
                            // Process data similar to ConcessionLocationController
                            const processedData = processConcessionData(data, dateToFetch);
                            resolve(processedData);
                        } catch (error) {
                            console.error(`Error processing concession data for ${dateToFetch}:`, error);
                            reject(error);
                        }
                    } else {
                        console.error('No data received from API');
                        reject(new Error('invalid'));
                    }
                },
                authCtx.token
            );
        });

        concessionDataPromise
            .then((processedResult) => {
                const dataToCache = {
                    processedData: processedResult,
                };

                setOverviewState((prevState) => ({
                    ...prevState,
                    concessionDataList: [...prevState.concessionDataList, processedResult],
                    loading: false,
                }));
            })
            .catch((error) => {
                console.error("Error fetching concession data for graph:", error);
                setOverviewState((prevState) => ({ ...prevState, loading: false }));
            });
    };

    // Process concession data similar to ConcessionLocationController
    const processConcessionData = (data, dateToFetch) => {
        const processedHistoricalQueueData = [];
        const processedHistoricalTransactionData = [];
        const processedHistoricalCountData = [];
        const latestQueueTimes = {};
        const latestSectionTransactionTimes = {};
        const latestSectionCounts = {};

        let activeCount = 0;
        let totalCount = 0;

        data.forEach(sectionData => {
            const sectionId = Object.keys(sectionData)[0];
            const dataPoints = sectionData[sectionId];

            if (dataPoints && dataPoints.length > 0) {
                const latestPoint = dataPoints[dataPoints.length - 1];
                
                if (isCountMode) {
                    // COUNT MODE: Focus on people count data
                    latestSectionCounts[sectionId] = latestPoint.peopleCount || 0;
                    
                    if ((latestPoint.peopleCount || 0) > 0) {
                        activeCount++;
                    }
                    totalCount++;

                    const countDataPoints = dataPoints.map(point => ({
                        x: toVenueTime(point.time, currentLocation.timeZone),
                        y: point.peopleCount || 0
                    }));

                    processedHistoricalCountData.push({
                        section: sectionId,
                        data: countDataPoints
                    });

                } else {
                    // QUEUE MODE: Process queue and transaction data
                    latestQueueTimes[sectionId] = latestPoint.queueTime;
                    latestSectionTransactionTimes[sectionId] = latestPoint.transactionTime;

                    if (latestPoint.queueTime > 0) {
                        activeCount++;
                    }
                    totalCount++;

                    const queueDataPoints = dataPoints.map(point => ({
                        x: toVenueTime(point.time, currentLocation.timeZone),
                        y: point.queueTime,
                        overflow: point.overflow || 0
                    }));

                    const transactionDataPoints = dataPoints.map(point => ({
                        x: toVenueTime(point.time, currentLocation.timeZone),
                        y: point.transactionTime || 0
                    }));

                    processedHistoricalQueueData.push({
                        section: sectionId,
                        data: queueDataPoints
                    });

                    processedHistoricalTransactionData.push({
                        section: sectionId,
                        data: transactionDataPoints
                    });
                }
            }
        });

        // Calculate averages similar to ConcessionLocationController
        if (isCountMode) {
            const activeCounts = Object.values(latestSectionCounts).filter(count => count > 0);
            const avgPeopleCount = activeCounts.length > 0 
                ? Math.round(activeCounts.reduce((a, b) => a + b, 0) / activeCounts.length) 
                : 0;

            return {
                date: dateToFetch,
                type: 'count',
                averagePeopleCount: avgPeopleCount,
                sectionCounts: latestSectionCounts,
                activeSections: activeCount,
                totalSections: totalCount,
                historicalCountData: processedHistoricalCountData
            };
        } else {
            const activeTimes = Object.values(latestQueueTimes).filter(time => time > 0);
            const avgQueueTime = activeTimes.length > 0 
                ? Math.round(activeTimes.reduce((a, b) => a + b, 0) / activeTimes.length) 
                : 0;

            const activeTransactionTimes = Object.values(latestSectionTransactionTimes).filter(time => time > 0);
            const avgTransactionTime = activeTransactionTimes.length > 0 
                ? Math.round(activeTransactionTimes.reduce((a, b) => a + b, 0) / activeTransactionTimes.length) 
                : 0;

            return {
                date: dateToFetch,
                type: 'queue',
                averageQueueTime: avgQueueTime,
                averageTransactionTime: avgTransactionTime,
                sectionQueueTimes: latestQueueTimes,
                sectionTransactionTimes: latestSectionTransactionTimes,
                activeSections: activeCount,
                totalSections: totalCount,
                historicalQueueData: processedHistoricalQueueData,
                historicalTransactionData: processedHistoricalTransactionData
            };
        }
    };

    const handleClearSelectedGames = useCallback(() => {
        setOverviewState((prevState) => ({
            ...prevState,
            selectedDates: [],
            concessionDataList: [],
            gameTitlesList: [],
            gameStartTimes: [],
        }));
    }, []);

    // Time range control handlers
    const handleStartOffsetChange = useCallback((newStartOffset) => {
        setTimeRange(prev => ({
            ...prev,
            startOffset: newStartOffset,
            isFullRange: false
        }));
    }, []);

    const handleEndOffsetChange = useCallback((newEndOffset) => {
        setTimeRange(prev => ({
            ...prev,
            endOffset: newEndOffset,
            isFullRange: false
        }));
    }, []);

    const handleResetTimeRange = useCallback(() => {
        setTimeRange({
            startOffset: -180, // -3 hours
            endOffset: 240,    // +4 hours
            isFullRange: false
        });
    }, []);

    // Update filteredGames to filter by the currently selected season
    const filteredGames = games
        .filter((x) => new Date() > moment(x.dates[0].date))
        .filter((x) => moment(x.dates[0].date).format("YYYY") === currentSeason);

    const getButtonStyle = useCallback((isSelected, selectedColor) => ({
        backgroundColor: isSelected ? selectedColor : "",
        color: isSelected ? "#ffffff" : "",
    }), []);

    // Calculate filtered summary metrics from concessionDataList using time range
    // Uses per-game averages so each game is weighted equally
    const { summaryMetrics, gamesWithNoData } = useMemo(() => {
        if (overviewState.concessionDataList.length === 0) {
            return {
                summaryMetrics: {
                    averagePeopleCount: 0,
                    averageQueueTime: 0,
                    averageTransactionTime: 0,
                    peakAverageQueueTime: 0,
                    peakAveragePeopleCount: 0
                },
                gamesWithNoData: []
            };
        }

        // Per-game computed values
        const gameAveragePeopleCounts = [];
        const gamePeakPeopleCounts = [];

        const gameAverageQueueTimes = [];
        const gameAverageTransactionTimes = [];
        const gamePeakQueueTimes = [];

        const noDataGames = [];

        overviewState.concessionDataList.forEach((gameData, gameIndex) => {
            const gameStartTime = overviewState.gameStartTimes[gameIndex];
            const gameTitle = overviewState.gameTitlesList[gameIndex];

            // Filter the game data based on time range
            const filteredGameData = filterDataByTimeRange(gameData, gameStartTime);

            let hasDataInRange = false;

            if (isCountMode && filteredGameData.historicalCountData) {
                // --- COUNT MODE ---
                const gameReadings = [];
                let gameMax = 0;

                filteredGameData.historicalCountData.forEach(sectionData => {
                    sectionData.data.forEach(point => {
                        if (point.y > 0) {
                            gameReadings.push(point.y);
                            gameMax = Math.max(gameMax, point.y);
                            hasDataInRange = true;
                        }
                    });
                });

                if (gameReadings.length > 0) {
                    const gameAvg = gameReadings.reduce((a, b) => a + b, 0) / gameReadings.length;
                    gameAveragePeopleCounts.push(gameAvg);
                }
                if (gameMax > 0) {
                    gamePeakPeopleCounts.push(gameMax);
                }

            } else if (!isCountMode) {
                // --- QUEUE MODE ---
                const queueReadings = [];
                const transactionReadings = [];
                let gameMaxQueue = 0;

                if (filteredGameData.historicalQueueData) {
                    filteredGameData.historicalQueueData.forEach(sectionData => {
                        sectionData.data.forEach(point => {
                            if (point.y > 0) {
                                queueReadings.push(point.y);
                                gameMaxQueue = Math.max(gameMaxQueue, point.y);
                                hasDataInRange = true;
                            }
                        });
                    });
                }

                if (filteredGameData.historicalTransactionData) {
                    filteredGameData.historicalTransactionData.forEach(sectionData => {
                        sectionData.data.forEach(point => {
                            if (point.y > 0) {
                                transactionReadings.push(point.y);
                                hasDataInRange = true;
                            }
                        });
                    });
                }

                if (queueReadings.length > 0) {
                    const gameAvgQueue = queueReadings.reduce((a, b) => a + b, 0) / queueReadings.length;
                    gameAverageQueueTimes.push(gameAvgQueue);
                }
                if (transactionReadings.length > 0) {
                    const gameAvgTx = transactionReadings.reduce((a, b) => a + b, 0) / transactionReadings.length;
                    gameAverageTransactionTimes.push(gameAvgTx);
                }
                if (gameMaxQueue > 0) {
                    gamePeakQueueTimes.push(gameMaxQueue);
                }
            }

            if (!hasDataInRange) {
                noDataGames.push(gameTitle);
            }
        });

        // Average the per-game values together
        const avg = (arr) => arr.length > 0
            ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length)
            : 0;

        return {
            summaryMetrics: {
                averagePeopleCount: avg(gameAveragePeopleCounts),
                averageQueueTime: avg(gameAverageQueueTimes),
                averageTransactionTime: avg(gameAverageTransactionTimes),
                peakAverageQueueTime: avg(gamePeakQueueTimes),
                peakAveragePeopleCount: avg(gamePeakPeopleCounts)
            },
            gamesWithNoData: noDataGames
        };
    }, [overviewState.concessionDataList, overviewState.gameStartTimes, overviewState.gameTitlesList, isCountMode, filterDataByTimeRange]);

    // Prepare filtered data for charts
    const filteredChartData = useMemo(() => {
        return overviewState.concessionDataList.map((gameData, index) => {
            const gameStartTime = overviewState.gameStartTimes[index];
            return filterDataByTimeRange(gameData, gameStartTime);
        });
    }, [overviewState.concessionDataList, overviewState.gameStartTimes, filterDataByTimeRange]);

    // Utility function to format transaction time from seconds
    const formatTransactionTime = useCallback((seconds) => {
        if (typeof seconds !== 'number' || seconds < 0) return '0s';
      
        const totalSeconds = Math.round(seconds);
      
        if (totalSeconds < 60) {
          return `${totalSeconds}s`;
        }
      
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;
      
        if (remainingSeconds === 0) {
          return `${minutes}m`;
        }
      
        return `${minutes}m ${remainingSeconds}s`;
    }, []);

    // ---- Per-game comparison (looked up by date so titles/start times always match the right game) ----
    const overThreshold = isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX;
    const gameCards = useMemo(() => {
        return overviewState.concessionDataList.map((gameData, dataIndex) => {
            const selIndex = overviewState.selectedDates.indexOf(gameData.date);
            const filtered = filterDataByTimeRange(gameData, overviewState.gameStartTimes[selIndex]);
            const main = (isCountMode ? filtered.historicalCountData : filtered.historicalQueueData) || [];
            const txn = filtered.historicalTransactionData || [];
            const avgSeries = averageSeries(seriesBySection(main));

            const readings = main.flatMap((s) => s.data).filter((p) => p.y > 0);
            const txnReadings = txn.flatMap((s) => s.data).filter((p) => p.y > 0);
            const mean = (arr) => (arr.length ? Math.round(arr.reduce((sum, p) => sum + p.y, 0) / arr.length) : 0);
            const peakOfPoints = (points) => points.reduce(
                (best, p) => (!best || p.y > best.y || (p.y === best.y && p.overflow && !best.overflow) ? p : best),
                null
            );

            // Minutes from this game's start (fallback: from its first reading) for lining games up
            const startTime = overviewState.gameStartTimes[selIndex];
            const firstPoint = main.find((sec) => sec.data.length)?.data[0];
            const startMinutes = startTime
                ? gameStartTimeToMinutes(startTime)
                : firstPoint ? timestampToMinutes(firstPoint.x) : 0;

            // Per-section numbers for the section comparison
            const sectionThreshold = isCountMode ? 30 : QUEUE_TIME_GOOD_MAX;
            const sections = {};
            main.forEach((sec) => {
                const positive = sec.data.filter((p) => p.y > 0);
                const series = seriesBySection([sec])[sec.section] || [];
                const peak = peakOfPoints(positive.length ? positive : sec.data);
                const txnSec = txn.find((t) => t.section === sec.section);
                const txnPositive = txnSec ? txnSec.data.filter((p) => p.y > 0) : [];
                sections[sec.section] = {
                    hasData: positive.length > 0,
                    avg: mean(positive),
                    peak: peak?.y ?? 0,
                    peakOverflow: !isCountMode && !!peak?.overflow,
                    over: minutesOver(series, sectionThreshold, !isCountMode).minutes,
                    overflow: isCountMode ? 0 : overflowSummary(series).minutes,
                    txnAvg: mean(txnPositive),
                    offsetSeries: sec.data.map((p) => ({
                        t: timestampToMinutes(p.x) - startMinutes,
                        y: p.y,
                        o: p.overflow ? 1 : 0,
                    })),
                };
            });
            const overflowSections = Object.entries(sections)
                .filter(([, m]) => m.overflow > 0)
                .map(([id, m]) => ({ id, minutes: m.overflow }))
                .sort((a, b) => b.minutes - a.minutes);
            const busiest = Object.entries(sections)
                .filter(([, m]) => m.hasData)
                .sort((a, b) => b[1].avg - a[1].avg)[0];

            // When the peak happened, in minutes from this game's start
            const overallPeak = peakOfPoints(readings);
            const peakOffset = overallPeak && startTime
                ? timestampToMinutes(overallPeak.x) - gameStartTimeToMinutes(startTime)
                : null;

            // Game start as a timestamp on the same clock as the chart points
            const gameStartTs = startTime ? new Date(`${gameData.date}T${startTime}:00`).getTime() : null;

            const title = overviewState.gameTitlesList[selIndex] || '';
            return {
                overflowSections,
                gameStartTs,
                peakOffset,
                sections,
                busiest: busiest ? { id: busiest[0], avg: busiest[1].avg } : null,
                date: gameData.date,
                title: title.includes('undefined') ? '' : title,
                color: overviewColors[dataIndex] || '#49AFA0',
                hasData: readings.length > 0,
                avg: mean(readings),
                peak: peakOfPoints(readings),
                over: minutesOver(avgSeries, overThreshold, !isCountMode),
                overflow: isCountMode ? { minutes: 0, episodes: 0 } : overflowSummary(avgSeries),
                txnAvg: mean(txnReadings),
                txnPeak: peakOfPoints(txnReadings),
                spark: avgSeries,
            };
        });
    }, [overviewState.concessionDataList, overviewState.selectedDates, overviewState.gameStartTimes, overviewState.gameTitlesList, filterDataByTimeRange, isCountMode, overThreshold, timestampToMinutes, gameStartTimeToMinutes]);

    // Series + game entry for a selected date (used by the chips' remove buttons)
    const findGameByDate = (date) => {
        for (const series of games) {
            const gameDate = series.dates?.find((d) => d.date === date);
            if (gameDate) return { series, gameDate };
        }
        return null;
    };

    // Button colors follow the chart order (data load order), so buttons, lines and tiles always match
    const gameColorFor = (date) => {
        const dataIndex = overviewState.concessionDataList.findIndex((d) => d.date === date);
        return overviewColors[dataIndex >= 0 ? dataIndex : overviewState.selectedDates.indexOf(date)] || "#49AFA0";
    };

    const cardsWithData = gameCards.filter((g) => g.hasData);
    // Selected games whose data hasn't arrived yet (shown as placeholders so nothing jumps)
    const pendingDates = overviewState.selectedDates.filter(
        (date) => !overviewState.concessionDataList.some((d) => d.date === date)
    );
    const matrixSections = (currentLocation?.sections || []).map((sec) => String(sec.sectionId));
    const insights = buildInsights({ games: gameCards, sections: matrixSections, isCountMode, formatTransactionTime });
    const avgOf = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const avgOverMinutes = avgOf(cardsWithData.map((g) => g.over.minutes));
    const avgOverShare = avgOf(cardsWithData.map((g) => g.over.share));
    const overflowGames = cardsWithData.filter((g) => g.overflow.minutes > 0);
    const totalOverflow = overflowGames.reduce((sum, g) => sum + g.overflow.minutes, 0);
    const overflowBySection = Object.values(
        cardsWithData.flatMap((g) => g.overflowSections || []).reduce((acc, { id, minutes }) => {
            acc[id] = { id, minutes: (acc[id]?.minutes || 0) + minutes };
            return acc;
        }, {})
    ).sort((a, b) => b.minutes - a.minutes);
    const sectionList = (list) => `${list.length === 1 ? 'Section' : 'Sections'} ${list.map((x) => `${x.id} (${formatDuration(x.minutes)})`).join(', ')}`;
    const hasSelection = overviewState.selectedDates.length > 0;

    return (
        <div>
            {overviewState.loading && (
                <div
                    style={{ position: "fixed", top: 0, left: 300, right: 0, zIndex: 9999 }}
                >
                    <LinearProgress
                        sx={{
                            backgroundColor: "rgba(2, 166, 147, 0.25)",
                            height: 4,
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "rgb(2, 166, 147)",
                            },
                        }}
                    />
                </div>
            )}

            {/* Compare bar: which games (chips + Add game dropdown) and the time window */}
            <div className="mb-4 cx-ov-bar-wrap" ref={barRef}>
                <Panel className="cx-ov-bar">
                    <div className="cx-ov-bar-row">
                        <div className="cx-ov-bar-games">
                            <div className="cx-ov-bar-label">
                                Games
                                <span className="cx-ov-count">{overviewState.selectedDates.length} of 4</span>
                            </div>
                            <div className="cx-ov-chips">
                                {overviewState.selectedDates.map((date) => {
                                    const found = findGameByDate(date);
                                    const pending = pendingDates.includes(date);
                                    const opponent = found ? getTeam(currentLocation.league, found.series.teamID)?.name : null;
                                    return (
                                        <span key={date} className={`cx-ov-chip cx-fade-in${pending ? ' pending' : ''}`}>
                                            <span className="cx-ov-swatch" style={{ background: gameColorFor(date) }} />
                                            <span className="cx-ov-chip-text">
                                                {moment(date).format('MMM D')}
                                                {opponent && <span className="cx-ov-chip-sub"> · {opponent}</span>}
                                            </span>
                                            {pending && <span className="cx-report-spinner cx-ov-chip-spinner" aria-hidden="true" />}
                                            <button
                                                type="button"
                                                className="cx-ov-chip-remove"
                                                aria-label={`Remove ${moment(date).format('MMM D')}`}
                                                disabled={overviewState.loading || !found}
                                                onClick={() => found && handleOverviewGameClick(found.gameDate, found.series)}
                                            >
                                                <Icon name="x" />
                                            </button>
                                        </span>
                                    );
                                })}
                                <div className="cx-ov-add" ref={pickerRef}>
                                    <button
                                        type="button"
                                        className="cx-ov-add-btn"
                                        onClick={() => setPickerOpen((o) => !o)}
                                        disabled={overviewState.selectedDates.length >= 4 && !pickerOpen}
                                        aria-expanded={pickerOpen}
                                    >
                                        <Icon name="plus-lg" />
                                        {overviewState.selectedDates.length >= 4 ? 'Max 4 games' : hasSelection ? 'Add game' : 'Add games to compare'}
                                    </button>
                                    {pickerOpen && (
                                        <div className="cx-ov-picker" role="dialog" aria-label="Pick games">
                                            <div className="cx-ov-picker-head">
                                                <span>Pick up to 4 games</span>
                                                <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={() => setPickerOpen(false)}>
                                                    <Icon name="x-lg" />
                                                </button>
                                            </div>
                                            {/* Season Selector */}
                                            <SeasonSelector
                                                games={games}
                                                currentSeason={currentSeason}
                                                setCurrentSeason={setCurrentSeason}
                                            />

                                            <div className="cx-game-list">
                                                {filteredGames.map((game, gameIndex) => {
                                                    const startDate = game.dates?.[0]?.date;
                                                    const endDate = game.dates?.[game.dates.length - 1]?.date;

                                                    return (
                                                        <div key={`game-${game.teamID}-${gameIndex}`} className="cx-game-row">
                                                            {/* Game Info */}
                                                            <div className="cx-game-info">
                                                                <img
                                                                    alt="Opponent"
                                                                    width={22}
                                                                    height={22}
                                                                    src={getTeam(currentLocation.league, game.teamID)?.imgURL}
                                                                />
                                                                <span className="cx-game-name">
                                                                    {getTeam(currentLocation.league, game?.teamID)?.name}
                                                                </span>
                                                                <span className="cx-game-dates">
                                                                    {moment(startDate).format("MMM D")} - {moment(endDate).format("MMM D")}
                                                                </span>
                                                            </div>

                                                            {/* Buttons */}
                                                            <div className="cx-game-buttons">
                                                                {game.dates.map((gameDate, dateIndex) => {
                                                                    const isSelected = overviewState.selectedDates.includes(gameDate.date);
                                                                    const selectedColor = isSelected ? gameColorFor(gameDate.date) : "";
                                                                    const buttonStyle = getButtonStyle(isSelected, selectedColor);

                                                                    return (
                                                                        new Date() > moment(gameDate.date) && (
                                                                            <Fragment key={gameDate.date}>
                                                                                <button
                                                                                    disabled={overviewState.loading}
                                                                                    onClick={() => {
                                                                                        if (overviewState.selectedDates.length >= 4 && !isSelected) {
                                                                                            alert("Maximum amount of games is 4.");
                                                                                        } else {
                                                                                            handleOverviewGameClick(gameDate, game);
                                                                                        }
                                                                                    }}
                                                                                    className={`btn cx-game-btn${isSelected ? ' selected' : ''}`}
                                                                                    style={{ ...buttonStyle, borderColor: isSelected ? selectedColor : undefined }}
                                                                                >
                                                                                    {dateIndex + 1}
                                                                                </button>
                                                                            </Fragment>
                                                                        )
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {hasSelection && (
                                    <button type="button" className="cx-ov-clear" onClick={handleClearSelectedGames}>
                                        Clear
                                    </button>
                                )}

                            </div>
                        </div>
                        <div className="cx-ov-bar-window">
                            <TimeWindow
                                compact
                                startOffset={timeRange.startOffset}
                                endOffset={timeRange.endOffset}
                                onStartChange={handleStartOffsetChange}
                                onEndChange={handleEndOffsetChange}
                                onPreset={(start, end) => setTimeRange({ startOffset: start, endOffset: end, isFullRange: false })}
                                info={<InfoTip title="Time window" text="Every number and chart compares the games over this window, measured from each game's start." />}
                                action={hasSelection ? (
                                    <div className="cx-report-slot">
                                        <ConcessionOverviewReportGenerator
                                            overviewState={overviewState}
                                            summaryMetrics={summaryMetrics}
                                            gamesWithNoData={gamesWithNoData}
                                            timeRange={timeRange}
                                            currentLocation={currentLocation}
                                            isCountMode={isCountMode}
                                            formatTransactionTime={formatTransactionTime}
                                            filteredChartData={filteredChartData}
                                            authCtx={authCtx}
                                            gameCards={gameCards}
                                            insights={insights}
                                            sections={matrixSections}
                                            pendingCount={pendingDates.length}
                                        />
                                    </div>
                                ) : null}
                            />
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Comparison: full width */}
            <div className="mb-4">
                    {/* Comparison animates in on the first game and out after the last one is removed */}
                    <Presence
                        show={hasSelection}
                        className="cx-ov-presence"
                        fallback={
                            <div className="cx-empty-state cx-ov-empty-main">
                                <Icon name="bar-chart-line" />
                                <div className="cx-empty-title">No games selected</div>
                                <div>Use <strong>Add games to compare</strong> above to pick up to 4 games and compare queues, overflow and transactions side by side.</div>
                            </div>
                        }
                    >
                        <div className="cx-ov-main">
                            {/* The biggest things to know, without clicking through */}
                            {insights.length > 0 && (
                                <Panel>
                                    <PanelHeader
                                        title="What stands out"
                                        subtitle="Key findings across the selected games, most important first."
                                    />
                                    <OverviewInsights insights={insights} />
                                </Panel>
                            )}

                            {/* Warning for games with no data in selected range */}
                            {gamesWithNoData.length > 0 && (
                                <div className="cx-notice">
                                    <Icon name="exclamation-triangle-fill" />
                                    <span>
                                        <strong>No data in this time window:</strong> {gamesWithNoData.join(', ')}
                                    </span>
                                </div>
                            )}

                            {/* Headline numbers across the selected games */}
                            <div className="cx-kpi-grid cx-kpi-grid-auto">
                                <KpiCard
                                    label={isCountMode ? 'Average count' : 'Average queue'}
                                    info={isCountMode
                                        ? "Average count between all sections across all selected games during the specified time range. Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval."
                                        : "Average queue between all sections across all selected games during the specified time range. Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval."}
                                    accent={STATUS_COLORS[isCountMode
                                        ? getPeopleCountStatus(summaryMetrics.averagePeopleCount, COUNT_AVG_LOW, COUNT_AVG_HIGH)
                                        : getQueueTimeStatus(summaryMetrics.averageQueueTime)]}
                                    value={isCountMode
                                        ? <>{summaryMetrics.averagePeopleCount}<span className="cx-unit">people</span></>
                                        : <>{summaryMetrics.averageQueueTime}<span className="cx-unit">mins</span></>}
                                >
                                    <div className="cx-kpi-foot">Per game, averaged</div>
                                </KpiCard>
                                <KpiCard
                                    label={isCountMode ? 'Average peak count' : 'Average peak queue'}
                                    info={isCountMode
                                        ? "The peak count recorded from each game (across all sections) averaged together across selected games in the time range."
                                        : "The peak queue time recorded from each game (across all sections) averaged together. Shows the typical worst-case queue time across selected games in the time range."}
                                    accent={STATUS_COLORS[isCountMode
                                        ? getPeopleCountStatus(summaryMetrics.peakAveragePeopleCount, COUNT_AVG_LOW, COUNT_AVG_HIGH)
                                        : getQueueTimeStatus(summaryMetrics.peakAverageQueueTime)]}
                                    value={isCountMode
                                        ? <>{summaryMetrics.peakAveragePeopleCount}<span className="cx-unit">people</span></>
                                        : <>{summaryMetrics.peakAverageQueueTime}<span className="cx-unit">mins</span></>}
                                >
                                    <div className="cx-kpi-foot">Typical worst moment</div>
                                </KpiCard>
                                <KpiCard
                                    label={isCountMode ? 'Busy time' : 'Time over target'}
                                    info={isCountMode
                                        ? `Average time per game the average count was above ${COUNT_AVG_HIGH} people. Counts actual time from every reading; charts show the highest reading in each interval, so they can look slightly higher.`
                                        : `Average time per game the average queue was ${QUEUE_TIME_GOOD_MAX} min or more (overflow counts as over). Counts actual time from every reading; charts show the highest reading in each interval, so they can look slightly higher.`}
                                    accent={avgOverMinutes > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good}
                                    value={formatDuration(avgOverMinutes)}
                                >
                                    <div className="cx-kpi-sub">{Math.round(avgOverShare * 100)}% of the window</div>
                                    <div className="cx-kpi-foot">Per game, averaged</div>
                                </KpiCard>
                                {!isCountMode && (
                                    <KpiCard
                                        label="Overflow"
                                        info="Total overflow time across the compared games in this window. Counts actual time from every reading; charts show the highest reading in each interval, so they can look slightly higher."
                                        accent={totalOverflow > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good}
                                        value={totalOverflow > 0 ? formatDuration(totalOverflow) : 'None'}
                                    >
                                        <div className="cx-kpi-sub">
                                            {totalOverflow > 0
                                                ? `In ${overflowGames.length} of ${cardsWithData.length} games`
                                                : 'No overflow in any game'}
                                        </div>
                                        {overflowBySection.length > 0 && (
                                            <div className="cx-ov-overflow-where">
                                                <Icon name="exclamation-triangle-fill" />
                                                {sectionList(overflowBySection)}
                                            </div>
                                        )}
                                        {overflowGames.length > 0 && (
                                            <div className="cx-section-dots">
                                                {overflowGames.map((g) => (
                                                    <span key={g.date} className="cx-section-dot" style={{ background: g.color }} title={`${moment(g.date).format('MMM D')}: ${formatDuration(g.overflow.minutes)} overflow`}>
                                                        {moment(g.date).format('MMM D')}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </KpiCard>
                                )}
                                {!isCountMode && (
                                    <KpiCard
                                        label="Average transaction"
                                        info="Average transaction time between all sections across all selected games during the specified time range."
                                        value={formatTransactionTime(summaryMetrics.averageTransactionTime)}
                                    >
                                        <div className="cx-kpi-foot">Per game, averaged</div>
                                    </KpiCard>
                                )}
                            </div>

                            {/* One tile per game, in its chart color */}
                            <Panel>
                                <PanelHeader
                                    title={<>Game summaries <span className="cx-ov-scope">All sections averaged</span></>}
                                    subtitle={isCountMode
                                        ? 'Every section combined, per game, over the time window.'
                                        : 'Every section combined, per game, over the time window. Dashed line is the target.'}
                                />
                                <div className="cx-tile-grid cx-ov-slots">
                                    {gameCards.map((g) => {
                                        const status = isCountMode
                                            ? getPeopleCountStatus(g.avg, COUNT_AVG_LOW, COUNT_AVG_HIGH)
                                            : getQueueTimeStatus(g.avg);
                                        const peakOverflow = !!g.peak?.overflow;
                                        const details = isCountMode
                                            ? [
                                                { label: 'Peak (any section)', value: `${g.peak?.y ?? 0} people`, at: g.peak ? formatClock(new Date(g.peak.x)) : null },
                                                { label: 'Busy time', value: formatDuration(g.over.minutes) },
                                            ]
                                            : [
                                                { label: 'Peak (any section)', value: `${formatQueueValue(g.peak?.y ?? 0, peakOverflow)} min`, at: g.peak ? formatClock(new Date(g.peak.x)) : null, alert: peakOverflow },
                                                { label: 'Over target', value: formatDuration(g.over.minutes) },
                                                { label: 'Overflow duration', value: g.overflow.minutes > 0 ? formatDuration(g.overflow.minutes) : 'None', alert: g.overflow.minutes > 0 },
                                                { label: 'Times in overflow', value: `${g.overflow.episodes}`, alert: g.overflow.episodes > 0 },
                                                { label: 'Avg transaction', value: formatTransactionTime(g.txnAvg) },
                                                { label: 'Peak transaction', value: formatTransactionTime(g.txnPeak?.y || 0), at: g.txnPeak ? formatClock(new Date(g.txnPeak.x)) : null },
                                            ];
                                        return (
                                            <div key={g.date} className="cx-tile cx-fade-in" style={{ '--cx-status-color': g.color }}>
                                                <div className="cx-tile-head">
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="cx-tile-name">{moment(g.date).format('ddd, MMM D')}</div>
                                                        {g.title && <div className="cx-ov-game-sub">{g.title}</div>}
                                                    </div>
                                                    {g.overflow.minutes > 0
                                                        ? <OverflowPill label={`Overflow ${formatDuration(g.overflow.minutes)}`} />
                                                        : <StatusPill status={status} label={(isCountMode ? COUNT_STATUS_LABELS : QUEUE_STATUS_LABELS)[status]} />}
                                                </div>
                                                {g.hasData ? (
                                                    <>
                                                        <div>
                                                            <div className="cx-ov-tile-value" style={{ color: STATUS_COLORS[status] }}>
                                                                {g.avg}<span className="cx-unit">{isCountMode ? 'people' : 'mins'}</span>
                                                            </div>
                                                            <div className="cx-tile-caption">{isCountMode ? 'average count · all sections' : 'average queue · all sections'}</div>
                                                            {g.busiest && matrixSections.length > 1 && (
                                                                <div className="cx-ov-busiest">
                                                                    Busiest section: <strong>{g.busiest.id}</strong> · {g.busiest.avg} {isCountMode ? 'people' : 'min'} avg
                                                                </div>
                                                            )}
                                                            {g.overflowSections?.length > 0 && (
                                                                <div className="cx-ov-overflow-where">
                                                                    <Icon name="exclamation-triangle-fill" />
                                                                    <span>Overflow in <strong>{sectionList(g.overflowSections)}</strong></span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Sparkline
                                                            points={g.spark}
                                                            color={g.color}
                                                            target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
                                                            minMax={isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX * 1.5}
                                                            height={40}
                                                            formatValue={unitFormatter(isCountMode ? 'people' : 'min')}
                                                            markers={g.gameStartTs ? [{ t: g.gameStartTs, label: 'Game start' }] : []}
                                                        />
                                                        <TileDetails details={details} />
                                                    </>
                                                ) : (
                                                    <div className="cx-ov-tile-nodata">No data in this time window</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {/* Games still loading keep their slot */}
                                    {pendingDates.map((date) => (
                                        <div key={`pending-${date}`} className="cx-tile cx-ov-slot-loading cx-fade-in">
                                            <div className="cx-tile-head">
                                                <div className="cx-tile-name">{moment(date).format('ddd, MMM D')}</div>
                                            </div>
                                            <div className="cx-skel cx-skel-value" />
                                            <div className="cx-skel cx-skel-chart" />
                                            <div className="cx-skel cx-skel-line" />
                                            <div className="cx-skel cx-skel-line short" />
                                            <div className="cx-ov-slot-note">Loading game data…</div>
                                        </div>
                                    ))}
                                    {/* Unused slots, so adding or removing a game never reflows the grid */}
                                    {Array.from({ length: Math.max(0, 4 - overviewState.selectedDates.length) }).map((_, i) => (
                                        <button
                                            key={`empty-${i}`}
                                            type="button"
                                            className="cx-ov-slot-empty"
                                            onClick={openPicker}
                                        >
                                            <Icon name="plus-circle" />
                                            <span>Add a game</span>
                                        </button>
                                    ))}
                                </div>
                            </Panel>
                        </div>
                    </Presence>
            </div>

            {/* Section comparison charts (PDF report captures these) */}
            <Presence show={hasSelection} className="cx-ov-presence">
                <SectionTabbedCharts
                    games={gameCards}
                    formatTransactionTime={formatTransactionTime}
                    concessionDataList={overviewState.concessionDataList}
                    gameTitles={overviewState.gameTitlesList}
                    gameStartTimes={overviewState.gameStartTimes}
                    isCountMode={isCountMode}
                    timeRangeFilter={timeRange}
                    currentLocation={currentLocation}
                    authCtx={authCtx}
                    extension={getConcessionExtension(authCtx, currentLocation)}
                />
            </Presence>
        </div>
    );
}